import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { helmetJsonLdProp } from 'react-schemaorg';
import { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

import { getUri } from 'services/generic';
import { capitalize } from 'services/formatter';
import { Hierarchy } from 'types/Hierarchy';
import { ProductBreadcrumbType } from 'types/ProductPageV2/Products';

type BreadCrumbsJsonLdProps = {
  breadcrumbItems: Hierarchy[] | ProductBreadcrumbType[];
};

/**
 * Render BreadCrumbs JsonLD format
 */
const BreadCrumbsJsonLd = ({
  breadcrumbItems = [],
}: BreadCrumbsJsonLdProps): JSX.Element => {
  const { t } = useTranslation();

  const getFullUri = (url: string): string => {
    return window.location.protocol + '//' + window.location.host + getUri(url);
  };

  const items: Array<{ Label: string; Url: string }> = [
    {
      Label: t('LABEL_HOME'),
      Url: '/',
    },
    ...breadcrumbItems,
  ];

  let position = 1;
  const numberOfItems = items.length;
  const breadcrumb: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(item => {
      const isLastItem = numberOfItems === position;
      const result: ListItem = {
        '@type': 'ListItem',
        position: position++,
        name: isLastItem ? item.Label : capitalize(item.Label),
      };

      if (item.Url) {
        result.item = getFullUri(item.Url);
      }

      return result;
    }),
  };

  return <Helmet script={[helmetJsonLdProp<BreadcrumbList>(breadcrumb)]} />;
};

export default BreadCrumbsJsonLd;
